import { useCallback, useEffect, useRef, useState } from "react";
import {
  cancelSubscription,
  createPortalSession,
  DoorError,
  isEnded,
  mintCardTransaction,
  resumeSubscription,
  SUBSCRIPTION_ENDED,
  saveBillingDetails,
  setBillingEmail,
  type BillingContext,
} from "@/lib/api/organizations";
import { useDialog } from "components/hooks/useDialog";
import type { BillingDetailsSave } from "components/main/organizations/BillingDetailsDialog";

/// Card, portal, paperwork, cancel and resume share one error line; the plans
/// dialog keeps its own.
export function useBillingActions({
  orgId,
  billing,
  patchBilling,
  reloadBilling,
  refreshBillingContext,
}: {
  orgId: string;
  billing: BillingContext | null;
  patchBilling: (patch: (context: BillingContext) => BillingContext) => void;
  reloadBilling: () => void;
  refreshBillingContext: () => void;
}) {
  const cardForm = useDialog<string>();
  const details = useDialog<true, { message: string; field: string | null }>();
  const cancelDialog = useDialog();
  const [error, setError] = useState<string | null>(null);
  const cardOpening = useRef(false);
  const portalOpening = useRef(false);

  // The payer and the card move on a webhook that can land after
  // `confirmCardChange` gave up, so the dialog re-reads on every open.
  useEffect(() => {
    if (details.open) refreshBillingContext();
  }, [details.open, refreshBillingContext]);

  const openCard = useCallback(async () => {
    if (cardOpening.current) return;
    cardOpening.current = true;
    setError(null);
    const transaction = await mintCardTransaction(orgId);
    cardOpening.current = false;
    if (isEnded(transaction)) {
      setError(SUBSCRIPTION_ENDED);
      reloadBilling();
      return;
    }
    if (!transaction) {
      setError("The card form could not be opened. Please try again.");
      return;
    }
    cardForm.show(transaction);
  }, [cardForm, orgId, reloadBilling]);

  const openPortal = useCallback(async () => {
    if (portalOpening.current) return;
    portalOpening.current = true;
    setError(null);
    // Opened inside the gesture: a window.open after the await is popup-blocked.
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    const session = await createPortalSession(orgId);
    portalOpening.current = false;
    const url = session?.url ?? null;
    if (!url) {
      tab?.close();
      setError("The billing portal could not be opened. Please try again.");
      return;
    }
    if (tab) tab.location.href = url;
    else window.location.href = url;
  }, [orgId]);

  const openDetails = useCallback(() => {
    details.setError(null);
    details.show(true);
  }, [details]);

  /// Paperwork first, then the address it goes to, so a refused tax number
  /// leaves the email where it was.
  const saveDetails = useCallback(
    async ({ email, details: paperwork }: BillingDetailsSave) => {
      details.setBusy(true);
      details.setError(null);
      // A departed payer's card is still charged; the email change quiets the
      // callout, so the card form follows.
      const handover = email !== null && billing?.payer_left;
      try {
        if (Object.keys(paperwork).length > 0) {
          await saveBillingDetails(orgId, paperwork);
        }
        if (email !== null) {
          const result = await setBillingEmail(orgId, email);
          patchBilling((prev) => ({ ...prev, billed_to: result.billed_to }));
          if (handover) {
            await openCard();
          }
          reloadBilling();
        }
        details.close();
      } catch (err) {
        details.setError({
          message:
            err instanceof Error
              ? err.message
              : "Billing details could not be saved.",
          field: err instanceof DoorError ? err.field : null,
        });
      } finally {
        details.setBusy(false);
      }
    },
    [
      details,
      orgId,
      patchBilling,
      reloadBilling,
      billing?.payer_left,
      openCard,
    ],
  );

  const openCancel = useCallback(() => {
    setError(null);
    cancelDialog.show(true);
  }, [cancelDialog]);

  /// The mirror is patched from the response so the row changes on the click.
  const changeSubscription = useCallback(
    async (action: "cancel" | "resume") => {
      cancelDialog.setBusy(true);
      setError(null);
      const result =
        action === "cancel"
          ? await cancelSubscription(orgId)
          : await resumeSubscription(orgId);
      cancelDialog.setBusy(false);
      if (isEnded(result)) {
        cancelDialog.close();
        setError(SUBSCRIPTION_ENDED);
        reloadBilling();
        return;
      }
      if (!result) {
        setError(
          action === "cancel"
            ? "The subscription could not be cancelled. Please try again."
            : "The subscription could not be resumed. Please try again.",
        );
        return;
      }
      patchBilling((prev) =>
        prev.subscription
          ? {
              ...prev,
              subscription: {
                ...prev.subscription,
                scheduled_change: result.scheduled_change,
              },
            }
          : prev,
      );
      cancelDialog.close();
    },
    [cancelDialog, orgId, patchBilling, reloadBilling],
  );

  return {
    error,
    setError,
    cardForm,
    openCard,
    openPortal,
    details,
    openDetails,
    saveDetails,
    cancelDialog,
    openCancel,
    changeSubscription,
  };
}
